// Actions on the event registrations page that admins can show or hide per team role.
// Admins always see everything; `adminOnly` actions are never shown to other roles (the API refuses them).
export const ROLE_UI_ROLES = ['EDITOR', 'VIEWER'];

export const ROLE_UI_GROUPS = [
  {
    title: 'Pages',
    actions: [
      { id: 'registrationsPage', label: 'Registrations page', default: true },
      { id: 'accessCodesPage', label: 'Access codes page (invited groups)', default: true },
      { id: 'analytics', label: 'Analytics page', default: true },
    ],
  },
  {
    title: 'Toolbar',
    actions: [
      { id: 'onSpot', label: 'On-Spot Registration', default: true },
      { id: 'scanQr', label: 'Scan QR buttons', default: true },
      { id: 'certificateScan', label: 'Certificate scan button', default: true },
      { id: 'dynamicBadge', label: 'Dynamic Badge', default: false },
      { id: 'copyDeskLink', label: 'Copy Desk Link', default: true },
      { id: 'qrPdf', label: 'QR Codes PDF', default: true },
      { id: 'emailAttendees', label: 'Email Attendees', default: true },
      { id: 'inviteLinks', label: 'Invite Links', default: true },
      { id: 'scanStats', label: 'Scan stats sharing', default: false },
      { id: 'export', label: 'Export (CSV / Excel / ZIP)', default: false },
      { id: 'exportColumns', label: 'Export columns setup', default: false },
    ],
  },
  {
    title: 'More menu',
    actions: [
      { id: 'bulkUpload', label: 'Bulk Upload', default: false },
      { id: 'badgeConfig', label: 'Badge Config', default: false },
      { id: 'lockers', label: 'Lockers', default: true },
      { id: 'openDesk', label: 'Open Desk', default: true },
      { id: 'scanLinks', label: 'Scan link generator', default: false },
    ],
  },
  {
    title: 'Navigation & tabs',
    actions: [
      { id: 'backButton', label: 'Back to events', default: false },
      { id: 'certificatesTab', label: 'Certificates tab', default: false },
      { id: 'teamTab', label: 'Team tab', default: false },
    ],
  },
  {
    title: 'Registrations table',
    actions: [
      { id: 'tableColumns', label: 'Columns picker', default: false },
      { id: 'tableFilters', label: 'Filters', default: false },
      { id: 'viewDetails', label: 'View Details', default: true },
      { id: 'printBadge', label: 'Print Badge', default: true },
      { id: 'printCertificate', label: 'Print Certificate', default: true },
      { id: 'approveReject', label: 'Approve / Reject', default: true },
      { id: 'sendConfirmationEmail', label: 'Send Confirmation Email', default: true },
      { id: 'assignRfid', label: 'Assign RFID', default: true },
      { id: 'rowLockers', label: 'Row Lockers button', default: true },
      { id: 'delete', label: 'Delete registrations', default: false, adminOnly: true },
    ],
  },
];

export const ROLE_UI_ACTIONS = ROLE_UI_GROUPS.flatMap((group) => group.actions);

const ACTIONS_BY_ID = Object.fromEntries(ROLE_UI_ACTIONS.map((action) => [action.id, action]));

export const defaultHiddenActions = () => ROLE_UI_ACTIONS
  .filter((action) => !action.default || action.adminOnly)
  .map((action) => action.id);

/** Saved config for a role, or the defaults when the admin hasn't configured it. */
export const roleConfig = (roleUi, role) => {
  const saved = roleUi?.[role];
  return {
    hiddenActions: Array.isArray(saved?.hiddenActions) ? saved.hiddenActions : defaultHiddenActions(),
    searchMode: saved?.searchMode === 'AUTO' ? 'AUTO' : 'SUBMIT',
    listMode: saved?.listMode === 'SEARCH_ONLY' ? 'SEARCH_ONLY' : 'ALL',
  };
};

export const teamRole = (roles = []) => {
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (roles.includes('EDITOR')) return 'EDITOR';
  return 'VIEWER';
};

const EVENT_PAGES = [['registrationsPage', 'registrations'], ['accessCodesPage', 'access-codes'], ['analytics', 'analytics']];

/** Path segment of the first event page the role may open, or null when none is allowed. */
export const firstEventPage = (ui) => EVENT_PAGES.find(([id]) => ui.can(id))?.[1] || null;

/** What the signed-in user may see on an event page. */
export const resolveRoleUi = (roleUi, roles = []) => {
  const role = teamRole(roles);
  if (role === 'ADMIN') {
    return { role, isAdmin: true, can: () => true, searchMode: 'AUTO', listMode: 'ALL' };
  }
  const { hiddenActions, searchMode, listMode } = roleConfig(roleUi, role);
  const hidden = new Set(hiddenActions);
  return {
    role,
    isAdmin: false,
    can: (id) => !ACTIONS_BY_ID[id]?.adminOnly && !hidden.has(id),
    searchMode,
    listMode,
  };
};
